import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    Signal,
    ViewChild,
} from '@angular/core'
import { UserDateFormattingService } from '@app/core/_services/user-date-formatting.service'
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons'
import { FormsModule } from '@angular/forms'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { ToggleSwitchModule } from 'primeng/toggleswitch'
import { SelectModule } from 'primeng/select'
import { DatePickerModule } from 'primeng/datepicker'
import {
    BookSessionsService,
    ConfirmedSessionsService,
    FIVE_MINUTES_IN_MSECS,
    MeetingsService,
    UtilsService,
} from '@app/core/_services'
import { FeatureCheckService } from '@app/core/_services/feature-check.service'
import {
    SESSION_DURATION_25_MINS,
    SESSION_DURATION_50_MINS,
    SESSION_DURATION_75_MINS,
    SessionPreferenceFavorites,
    User,
} from '@app/core/_models'
import { combineLatestWith, filter, Subject, take, takeUntil } from 'rxjs'
import { UserService } from '@app/core/_services/user.service'
import { DialogService } from '@app/core/_services/dialog.service'
import { ErrorHandlerService } from '@app/core/_services/error-handler.service'
import { BookLimitCheckService } from '@app/core/_services/book-limit-check.service'
import { SessionSettingsService } from '@app/core/_services/session-settings.service'
import { WelcomeChecklistService } from '@app/core/_services/welcome-checklist.service'
import { RewardsReferralService } from '@app/core/_services/rewards-referral.service'
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min'
import { BookingSource } from '@app/core/_services/analytics.service'
import { InputTextModule } from 'primeng/inputtext'
import { CommonModule } from '@angular/common'
import { IconFieldModule } from 'primeng/iconfield'
import { InputIconModule } from 'primeng/inputicon'
import { ButtonModule } from 'primeng/button'
import { MODAL_VERIFY_EMAIL_BOOKING } from '@app/shared-dialogs/email-verify/email-verify-dialog.component'
import {
    BookingHubTaskFormComponent,
    QueuedTask,
} from '../booking-hub-task-form/booking-hub-task-form.component'
import { TaskService } from '@app/core/_services/task.service'
import { ToastService } from '@app/core/_services/toast.service'
import { forkJoin, of } from 'rxjs'
import { catchError } from 'rxjs/operators'
import { TimeUtilitiesService } from '@app/core/_services/time-utilities.service'

interface TimeOption {
    name: string
    value: string
    disabled: boolean
}

interface SessionTimeIntervals {
    sessionTime: number
    duration: number
    sessionEndWithCooldown: number
}

const LATE_BOOKING_GRACE_PERIOD_MINUTES = 5
const LATE_BOOKING_GRACE_PERIOD_MILLISECONDS = 5 * 60 * 1000

interface ScheduleSessionForm {
    date: Date | null
    selectedTimeValue: string
    recurrenceType: number
    recurrenceFrequency: number
    favoritesPreference: SessionPreferenceFavorites
    title: string | null
}

@Component({
    selector: 'fm-booking-hub-schedule-form',
    imports: [
        InputIconModule,
        IconFieldModule,
        CommonModule,
        InputTextModule,
        DatePickerModule,
        FormsModule,
        ButtonModule,
        FontAwesomeModule,
        ToggleSwitchModule,
        SelectModule,
        BookingHubTaskFormComponent,
    ],
    templateUrl: './booking-hub-schedule-form.component.html',
    styleUrl: './booking-hub-schedule-form.component.scss',
})
export class BookingHubScheduleFormComponent {
    @ViewChild('container') container: ElementRef
    @ViewChild('expandedContentContainer') expandedContentContainer: ElementRef
    @Input() minimumWaitBeforeDefaultTimeslot: number = 0
    @Output() onBookingCompleted: EventEmitter<void> = new EventEmitter<void>()

    private ngUnsubscribe: Subject<any> = new Subject<any>()
    private isWelcomeChecklistActive: Signal<boolean> =
        this.welcomeChecklistService.isWelcomeChecklistActive

    public expandSchedulePanel = false
    public expandMoreOptions = false

    // Time options
    public startOfWeekDay: number
    public timeSlotValues: TimeOption[] = []
    private readonly baseTimeSlotValues: TimeOption[] = []
    public selectedTime: TimeOption
    public minDate: Date
    public dateDisplayString: string
    public bookCtaTimeString: string

    // Scheduler - advanced
    faChevronUp = faChevronUp
    faChevronDown = faChevronDown
    recurType = [
        { value: 1, viewValue: 'Does Not Repeat', name: 'Do not repeat', icon: 'pi pi-circle' },
        { value: 3, viewValue: 'Repeat Weekly', name: ' Repeat weekly', icon: 'pi pi-refresh' },
        { value: 2, viewValue: 'Repeat Daily', name: ' Repeat daily', icon: 'pi pi-sun' },
    ]

    recurTimes = [
        { value: 1, viewValue: '1 Time', name: '1 Time' },
        { value: 2, viewValue: '2 Times', name: '2 Times' },
        { value: 3, viewValue: '3 Times', name: '3 Times' },
        { value: 4, viewValue: '4 Times', name: '4 Times' },
        { value: 5, viewValue: '5 Times', name: '5 Times' },
        { value: 6, viewValue: '6 Times', name: '6 Times' },
        { value: 7, viewValue: '7 Times', name: '7 Times' },
        { value: 8, viewValue: '8 Times', name: '8 Times' },
        { value: 9, viewValue: '9 Times', name: '9 Times' },
        { value: 10, viewValue: '10 Times', name: '10 Times' },
    ]

    public formModel: ScheduleSessionForm = {
        title: null,
        date: null,
        selectedTimeValue: null,
        recurrenceType: 1,
        recurrenceFrequency: 1,
        favoritesPreference: this.sessionSettingsService.getSessionSettingFavoritesPreference(),
    }
    private selectedSessionDuration: number
    public user: User
    public limitRemaining: number = 0

    private initialized: boolean = false
    public selectionInvalid: boolean = false

    public booking = false
    private busyTimeIntervals: SessionTimeIntervals[] = []

    public queuedTasks: QueuedTask[] = []

    constructor(
        public userService: UserService,
        private dialogService: DialogService,
        private bs: BookSessionsService,
        private errorHandlerService: ErrorHandlerService,
        public bookLimitCheckService: BookLimitCheckService,
        private sessionSettingsService: SessionSettingsService,
        private welcomeChecklistService: WelcomeChecklistService,
        private rewardsReferralService: RewardsReferralService,
        private confirmedSessionsService: ConfirmedSessionsService,
        private meetingsService: MeetingsService,
        private dateFormat: UserDateFormattingService,
        private utilsService: UtilsService,
        private featureCheckService: FeatureCheckService,
        private timeUtilities: TimeUtilitiesService,
    ) {
        // create timeslot values
        for (let millis = 0; millis < 1000 * 60 * 60 * 24; millis += 1000 * 60 * 15) {
            const timeString = this.dateFormat.format(millis, 'h:mma', 'UTC')
            const timeStringAmPm = this.dateFormat.format(millis, 'h:mm a', 'UTC', '12hr')
            this.baseTimeSlotValues.push({
                value: timeStringAmPm,
                name: timeString,
                disabled: false,
            })
        }
        this.timeSlotValues = [...this.baseTimeSlotValues]
    }

    ngOnInit() {
        this.userService.currentUser
            .pipe(
                combineLatestWith(
                    this.sessionSettingsService.sessionDurationSetting$,
                    this.meetingsService.meetingsBookingsSubject,
                ),
                filter(
                    ([currentUser, sessionDuration]) =>
                        currentUser?.timeZone != null && sessionDuration != null,
                ),
                takeUntil(this.ngUnsubscribe),
            )
            .subscribe(([currentUser, sessionDuration, bookings]) => {
                this.user = currentUser
                this.selectedSessionDuration = sessionDuration
                //this.eventsBookingsEvents = bookings.events ?? []
                this.startOfWeekDay = this.user.settings.startOfWeekDay
                const intervals = []
                for (const session of bookings.meetings) {
                    intervals.push({
                        sessionTime: session.startMs,
                        duration: session.durationMs,
                        sessionEndWithCooldown:
                            session.startMs +
                            session.durationMs +
                            this.getCooldownForSession(session.durationMs),
                    })
                }
                this.busyTimeIntervals = [...intervals].sort(
                    (left, right) => left.sessionTime - right.sessionTime,
                )
                if (!this.initialized) {
                    this.initialized = true
                    this.determineFirstAvailableTimeSlot()
                    this.timeSlotValues = [
                        ...this.updateTimeOptionsDisabled(this.selectedSessionDuration),
                    ]
                } else {
                    this.timeSlotValues = [
                        ...this.updateTimeOptionsDisabled(this.selectedSessionDuration),
                    ]
                    this.selectionInvalid = !this.isSelectedTimeValid()
                }
            })

        this.bookLimitCheckService.currentRemaining
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((limit) => {
                this.limitRemaining = limit
            })
    }

    onClickShowMoreOptions() {
        this.utilsService.animateHeightChange(this.container.nativeElement, () => {
            this.expandMoreOptions = !this.expandMoreOptions
        })
    }

    private determineFirstAvailableTimeSlot(): void {
        const canBookLate = this.featureCheckService.canUseNowSessions(this.user)
        const currentTime = moment.tz(this.user.timeZone).valueOf()

        let now = moment.tz(this.user.timeZone).seconds(0).milliseconds(0)
        now.add(this.minimumWaitBeforeDefaultTimeslot, 'minutes')
        now = this.roundUpToNextQuarterHour(now, canBookLate)

        let proposedTime = now.valueOf()

        // Ensure proposed time is not in the past (unless user can book late)
        if (!canBookLate && proposedTime < currentTime) {
            // Round up to the next quarter hour from current time
            const currentMoment = moment.tz(this.user.timeZone).seconds(0).milliseconds(0)
            const minutes = currentMoment.minutes()
            const nextQuarter = Math.ceil(minutes / 15) * 15
            if (nextQuarter >= 60) {
                proposedTime = currentMoment
                    .add(1, 'hour')
                    .minutes(0)
                    .seconds(0)
                    .milliseconds(0)
                    .valueOf()
            } else {
                proposedTime = currentMoment
                    .minutes(nextQuarter)
                    .seconds(0)
                    .milliseconds(0)
                    .valueOf()
            }
        }

        for (const session of this.busyTimeIntervals) {
            while (this.hasOverlap(proposedTime, session.sessionTime, session.duration)) {
                proposedTime = this.getNextQuarterAfterSession(
                    session.sessionTime,
                    session.duration,
                )
            }
        }

        this.formModel.selectedTimeValue = this.dateFormat.format(
            proposedTime,
            'h:mm a',
            this.user.timeZone,
            '12hr',
        )
        this.bookCtaTimeString =
            this.timeSlotValues.find(
                (element) => element.value === this.formModel.selectedTimeValue,
            )?.name || ''

        const initialDateTime = moment(proposedTime).tz(this.user.timeZone)
        const year = initialDateTime.year()
        const month = initialDateTime.month()
        const day = initialDateTime.date()
        // Use midday in local time to avoid timezone rollover
        this.formModel.date = new Date(year, month, day, 12, 0, 0)
        // Ensure minDate is slightly behind initial date (today)
        this.minDate = new Date(year, month, day, 11, 0, 0)
        this.dateDisplayString = moment(proposedTime).tz(this.user.timeZone).format('DD - MMM')
    }

    private roundUpToNextQuarterHour(
        time: moment.Moment,
        allowGracePeriod: boolean = false,
    ): moment.Moment {
        const grace = allowGracePeriod ? LATE_BOOKING_GRACE_PERIOD_MINUTES : 0
        const minutes = time.minutes()
        const slotMinutes = [0, 15, 30, 45, 60]

        for (let m of slotMinutes) {
            if (minutes < m + grace) {
                return time.minutes(m).seconds(0).milliseconds(0)
            }
        }

        return time.add(1, 'hour').minutes(0).seconds(0).milliseconds(0)
    }

    private hasOverlap(
        proposedStart: number,
        sessionStart: number,
        sessionDuration: number,
    ): boolean {
        const proposedEnd = proposedStart + this.selectedSessionDuration
        const sessionEnd = sessionStart + sessionDuration
        return proposedStart < sessionEnd && proposedEnd > sessionStart
    }

    private getCooldownForSession(sessionDuration: number): number {
        if (sessionDuration === SESSION_DURATION_25_MINS) {
            return FIVE_MINUTES_IN_MSECS
        } else if (sessionDuration === SESSION_DURATION_50_MINS) {
            return 2 * FIVE_MINUTES_IN_MSECS
        } else if (sessionDuration === SESSION_DURATION_75_MINS) {
            return 3 * FIVE_MINUTES_IN_MSECS
        }
        return 0
    }

    private getNextQuarterAfterSession(sessionTime: number, sessionDuration: number): number {
        const coolDown = this.getCooldownForSession(sessionDuration)
        return sessionTime + sessionDuration + coolDown
    }

    private updateTimeOptionsDisabled(selectedDuration: number): TimeOption[] {
        const canBookLate = this.featureCheckService.canUseNowSessions(this.user)
        const timeOptions = [...this.baseTimeSlotValues]

        if (!this.formModel.date || !this.user?.timeZone) {
            return timeOptions
        }

        const now = moment.tz(this.user.timeZone).valueOf()

        let sessionIndex = 0

        return timeOptions
            .map((option) => ({ option, optionStart: this.buildSessionTime(option.value) }))
            .filter(({ optionStart }) => Number.isFinite(optionStart))
            .sort((left, right) => left.optionStart - right.optionStart)
            .map(({ option, optionStart }) => {
                const optionEnd = optionStart + selectedDuration

                // Skip past sessions that end (including cooldown) before this option starts
                while (
                    sessionIndex < this.busyTimeIntervals.length &&
                    this.busyTimeIntervals[sessionIndex].sessionEndWithCooldown <= optionStart
                ) {
                    sessionIndex++
                }

                let disabled = false
                for (let j = sessionIndex; j < this.busyTimeIntervals.length; j++) {
                    const session = this.busyTimeIntervals[j]
                    const sessionStart = session.sessionTime
                    const sessionEnd = session.sessionEndWithCooldown

                    if (optionStart < sessionEnd && optionEnd > sessionStart) {
                        disabled = true
                        break
                    }

                    if (sessionStart >= optionEnd) break
                }

                // 👇 Override: if more than 5 minutes in the past, ensure it's disabled
                if (optionStart < now - LATE_BOOKING_GRACE_PERIOD_MILLISECONDS) {
                    disabled = true
                } else if (optionStart < now) {
                    // If past start time but within grace period, check if user can book late (same as Focus Now gating)
                    if (!canBookLate) {
                        disabled = true
                    }
                }

                return { ...option, disabled }
            })
    }

    isSelectedTimeValid(): boolean {
        if (!this.formModel.date || !this.formModel.selectedTimeValue || !this.user?.timeZone) {
            return false
        }

        const sessionTime = this.buildSessionTime()
        const now = moment().tz(this.user.timeZone).valueOf()
        const fiveMinutesAgo = now - LATE_BOOKING_GRACE_PERIOD_MILLISECONDS
        const isPastStartTime = sessionTime < now

        // If booking past start time, check if user has minimum required sessions (same as Focus Now gating)
        if (isPastStartTime) {
            const canBookLate = this.featureCheckService.canUseNowSessions(this.user)
            if (!canBookLate) {
                return false
            }
        }

        const timeIsNotTooFarInPast = sessionTime >= fiveMinutesAgo
        const selectedOption = this.timeSlotValues.find(
            (element) => element.value === this.formModel.selectedTimeValue,
        )
        const timeIsNotOverlapping = selectedOption != null && !selectedOption.disabled

        return timeIsNotTooFarInPast && timeIsNotOverlapping
    }

    private getHoursFromString(parsedTimeString): number {
        let hours
        if (parsedTimeString[1].slice(-2) === 'pm') {
            hours = +parsedTimeString[0]
            if (hours !== 12) {
                hours += 12
            }
        } else {
            hours = +parsedTimeString[0]
            if (hours === 12) {
                hours = 0
            }
        }
        return hours
    }

    private getMinutesFromString(parsedTimeString): number {
        return +parsedTimeString[1].slice(0, -2)
    }

    private buildSessionTime(timeValue: string = this.formModel.selectedTimeValue): number {
        if (!this.formModel.date || !timeValue || !this.user?.timeZone) {
            return Number.NaN
        }

        const parsedTimeString = timeValue.split(/:/)
        const hours = this.getHoursFromString(parsedTimeString)
        const minutes = this.getMinutesFromString(parsedTimeString)

        return (
            this.timeUtilities.getSessionStartMs(
                this.formModel.date,
                hours * 60 + minutes,
                this.user.timeZone,
            ) ?? Number.NaN
        )
    }

    onSubmit(formData) {
        if (
            this.booking ||
            this.selectionInvalid ||
            !this.formModel.date ||
            !this.formModel.selectedTimeValue
        ) {
            return
        }
        this.booking = true

        const numSessionsToBook = this.formModel.recurrenceFrequency
        const sessionTime = this.buildSessionTime()
        if (!Number.isFinite(sessionTime)) {
            this.booking = false
            return
        }
        const sessionTimes = this.busyTimeIntervals.map((interval) => interval.sessionTime)

        this.bs
            .bookRecurringSessions(
                sessionTime,
                this.formModel.recurrenceType,
                numSessionsToBook,
                this.user.timeZone,
                this.formModel.title,
                sessionTimes,
                this.selectedSessionDuration,
                this.formModel.favoritesPreference,
                this.sessionSettingsService.getSessionSettingActivityType(),
                this.sessionSettingsService.quietModeEnabled(),
                BookingSource.BookingHubScheduler,
            )
            .pipe(take(1))
            .subscribe(
                (sessions) => {
                    this.booking = false
                    let parsedRes = this.bs.parseBookResponse(sessions)
                    let message: string = ''

                    if (parsedRes['numSuccess'] > 0) {
                        message = 'Booking saved!'

                        if (parsedRes['numSuccess'] > 1) {
                            message = 'Bookings saved!'
                        }
                    }

                    /*
                    // TODO: create tasks in booking hub
                    if (this.queuedTasks.length > 0) {
                        this.createQueuedTasks(sessions)
                    }
                    */

                    this.onBookingCompleted.emit()

                    if (this.user.emailVerified === false) {
                        this.dialogService.openEmailVerificationDialog(
                            MODAL_VERIFY_EMAIL_BOOKING,
                            () => {
                                this.processParsedResult(parsedRes)
                            },
                        )
                    } else {
                        this.processParsedResult(parsedRes)
                    }
                },
                (error) => {
                    this.booking = false
                    this.errorHandlerService.handleErrorResponse(error)
                },
            )
    }

    processParsedResult(parsedRes) {
        if (parsedRes['planLimitReached'] === true) {
            this.dialogService.openPlanLimitReachedDialog()
        } else if (
            !this.isWelcomeChecklistActive() &&
            !this.user.properties.viewedSegmentationSurveyModal &&
            this.user.compSessions >= 3
        ) {
            this.dialogService.openSegmentationSurveyModal()
        } else if (
            !this.isWelcomeChecklistActive() &&
            this.rewardsReferralService.shouldSeeReferralPrompt(this.user)
        ) {
            this.dialogService.openReferralPromptModal()
        }
    }

    selectedTimeSlot(e) {
        this.selectionInvalid = !this.isSelectedTimeValid()
        this.bookCtaTimeString =
            this.timeSlotValues.find(
                (element) => element.value === this.formModel.selectedTimeValue,
            )?.name || ''
    }

    selectedRecurrenceType(e) {
        if (e != 1) {
            if (this.limitRemaining <= 10) {
                this.formModel.recurrenceFrequency = this.limitRemaining
            } else {
                this.formModel.recurrenceFrequency = 10
            }
        } else {
            this.formModel.recurrenceFrequency = 1
        }
    }

    public onDateSelected(e) {
        this.dateDisplayString = moment.utc(e).tz(this.user.timeZone).format('DD - MMM')
        this.timeSlotValues = [...this.updateTimeOptionsDisabled(this.selectedSessionDuration)]
        this.selectionInvalid = !this.isSelectedTimeValid()
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(null)
        this.ngUnsubscribe.complete()
    }
}
